import { useId } from 'react';

import { useTranslation } from 'react-i18next';

import type {
  EffectiveOpeningDayResponse,
  EffectiveOpeningHoursResponse
} from 'generated/index';

import { Alert } from '@mui/material';

import { Icon } from '@sdb/planning-ui';

import Tooltip from 'components/shared/Tooltip';

import {
  hasNoOpeningHours,
  sortDays,
  toWeekday
} from '../../utils/effectiveOpeningHours';
import type { EffectiveOpeningOverviewProps } from './EffectiveOpeningOverview.types';
import {
  StyledCell,
  StyledNoOpeningHours,
  StyledOverview,
  StyledRow,
  StyledTile,
  StyledTileTitle
} from './EffectiveOpeningOverview.styles';

/**
 * What applies on the chosen date, per day type and weekday, and where each answer comes from. Three
 * states look different on purpose: the plan unit's own line, an inherited answer (muted, with the
 * name of the plan unit it comes from), and no opening hours on the plan unit or any of its parents -
 * which means closed, but reads differently from a closure someone recorded.
 */
const EffectiveOpeningOverview = ({ weeks }: EffectiveOpeningOverviewProps) => {
  'use memo';
  const { t } = useTranslation('common', {
    keyPrefix: 'planUnitHome.openingHours'
  });
  const { t: tCommon } = useTranslation('common');
  const idPrefix = useId();

  const withoutOpeningHours = weeks.filter(hasNoOpeningHours);
  const recorded = weeks.filter((week) => !hasNoOpeningHours(week));

  const describe = (day: EffectiveOpeningDayResponse) => {
    if (day.source === 'NONE')
      return (
        <Tooltip title={t('notRecordedTooltip')}>
          <StyledNoOpeningHours>
            {t('notRecorded')}
            <Icon type="circle-info" fontSize="inherit" />
          </StyledNoOpeningHours>
        </Tooltip>
      );
    if (day.closed) return t('closed');
    return day.blocks
      .map((slot) =>
        [`${slot.startTime}–${slot.endTime}`, slot.description]
          .filter(Boolean)
          .join(' ')
      )
      .join(', ');
  };

  const origin = (day: EffectiveOpeningDayResponse) => {
    if (day.source === 'OWN') return t('sourceOwn');
    if (day.source === 'INHERITED')
      return t('sourceInherited', { name: day.sourceName ?? '' });
    return '';
  };

  const renderTile = (week: EffectiveOpeningHoursResponse) => {
    const titleId = `${idPrefix}-${week.dayType}`;
    return (
      <StyledTile
        key={week.dayType}
        variant="outlined"
        role="table"
        aria-labelledby={titleId}
      >
        <StyledTileTitle id={titleId} variant="subtitle2" component="h3">
          {t(`dayType.${week.dayType}`)}
        </StyledTileTitle>
        {sortDays(week.days).map((day) => {
          const muted = day.source !== 'OWN';
          return (
            <StyledRow key={day.dayOfWeek} role="row">
              <StyledCell role="rowheader" muted={muted}>
                {tCommon(`weekday.${toWeekday(day)}`)}
              </StyledCell>
              <StyledCell role="cell" muted={muted}>
                {describe(day)}
              </StyledCell>
              <StyledCell role="cell" muted={muted}>
                {origin(day)}
              </StyledCell>
            </StyledRow>
          );
        })}
      </StyledTile>
    );
  };

  return (
    <>
      {withoutOpeningHours.length > 0 && (
        <Alert severity="info">
          {t('dayTypesWithoutOpeningHours', {
            dayTypes: withoutOpeningHours
              .map((week) => t(`dayType.${week.dayType}`))
              .join(', ')
          })}
        </Alert>
      )}
      <StyledOverview>{recorded.map(renderTile)}</StyledOverview>
    </>
  );
};

export default EffectiveOpeningOverview;
